"use client"

import { useState, type ReactNode } from "react"
import { RotateCcw } from "lucide-react"

import { RestartOnboardingConfirmationDialog } from "@/components/onboarding/restart-onboarding-confirmation-dialog"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { useRestartSignupOnboardingMutation } from "@/hooks/mutations/signup-onboarding"
import { SIGNUP_ONBOARDING_PATH } from "@/lib/onboarding/constants"
import { useTranslations } from "next-intl"



type RestartOnboardingCardVariant = "default" | "compact"

export function RestartOnboardingCard({
  variant = "default",
}: {
  variant?: RestartOnboardingCardVariant
}) {
  const tUi = useTranslations("Onboarding.RestartOnboardingCard")

  const [confirmationOpen, setConfirmationOpen] = useState(false)
  const restart = useRestartSignupOnboardingMutation()

  const handleRestart = async () => {
    await restart.mutateAsync()
    window.location.assign(SIGNUP_ONBOARDING_PATH)
  }

  let card: ReactNode
  switch (variant) {
    case "compact":
      card = (
        <Card className="bg-muted/20 shadow-none">
          <CardContent className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
            <div className="min-w-0">
              <p className="text-sm font-medium"><span key={tUi("onboarding")}>{tUi("onboarding")}</span></p>
              <p className="text-xs text-muted-foreground">
                <span key={tUi("restartTheGuidedSetupWithoutChangingExistingAgents")}>{tUi("restartTheGuidedSetupWithoutChangingExistingAgents")}</span></p>
            </div>
            <RestartOnboardingButton
              compact
              onClick={() => setConfirmationOpen(true)}
            />
          </CardContent>
        </Card>
      )
      break
    case "default":
      card = (
        <Card>
          <CardHeader>
            <CardTitle className="break-words"><span key={tUi("onboarding")}>{tUi("onboarding")}</span></CardTitle>
            <CardDescription>
              <span key={tUi("runTheGuidedSetupAgainFromTheBeginning")}>{tUi("runTheGuidedSetupAgainFromTheBeginning")}</span></CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center justify-between gap-4">
            <p className="max-w-2xl text-sm text-muted-foreground">
              <span key={tUi("restartingClearsSavedWizardProgressButKeepsYour")}>{tUi("restartingClearsSavedWizardProgressButKeepsYour")}</span></p>
            <RestartOnboardingButton
              onClick={() => setConfirmationOpen(true)}
            />
          </CardContent>
        </Card>
      )
      break
    default: {
      const exhaustiveVariant: never = variant
      return exhaustiveVariant
    }
  }

  return (
    <>
      {card}

      <RestartOnboardingConfirmationDialog
        mode="self"
        open={confirmationOpen}
        pending={restart.isPending}
        onOpenChange={setConfirmationOpen}
        onConfirm={handleRestart}
      />
    </>
  )
}

function RestartOnboardingButton({
  compact = false,
  onClick,
}: {
  compact?: boolean
  onClick: () => void
}) {
  const tUi = useTranslations("Onboarding.RestartOnboardingCard")

  return (
    <Button
      type="button"
      variant="outline"
      size={compact ? "sm" : "default"}
      className={compact ? "h-auto min-h-8 max-w-full whitespace-normal text-xs" : "h-auto min-h-10 max-w-full whitespace-normal text-left"}
      onClick={onClick}
    >
      <RotateCcw className="mr-2 h-4 w-4" />
      <span key={tUi("restartOnboarding")}>{tUi("restartOnboarding")}</span></Button>
  )
}
